/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import { isFunction } from 'lodash';
import { Fragment, useCallback, useEffect, useState } from 'react';

import { AsyncOverlay, Document, MessageBar } from '@zapier/common-components';
import { useLink } from '@zapier/common-hooks';
import { SkeletonBlock, Spacer } from '@zapier/design-system';
import { usePaymentSource, PaymentApp } from '@zapier/payment';
import { usePromoPlans } from '@zapier/promotions';
import { Border, Colors } from '@zapier/design-system-revenue/src/constants';

import {
  useMaybeSendFullStoryEvent,
  useTrackingContext,
} from '@zapier/toolbox-tracking';

import {
  buildDowngradeWarningUrl,
  isFree,
} from '@zapier/design-system-revenue/src/utils';

import {
  useChangePlan,
  useCurrentAccountId,
  useInvoicePreview,
  usePendingPayment,
  usePlans,
  useSelectedCurrency,
  useCurrencyRuleset,
} from '@zapier/design-system-revenue/src/hooks';

import {
  ChangeType,
  Interval,
  PlanV2,
} from '@zapier/design-system-revenue/src/types';

import {
  ChangePlanCompleteModal,
  PendingPaymentWarningBar,
} from '@zapier/design-system-revenue/src/components/other';

import { CurrencyProvider } from '@zapier/design-system-revenue/src/contexts/CurrencyContext';

import { BillingOptions } from './BillingOptions';
import { ChangePlanButtons } from './ChangePlanButtons';
import { ChangePlanCurrencyDropdown } from './ChangePlanCurrencyDropdown';
import { ChangePlanHeader } from './ChangePlanHeader';
import { ChangePlanIndicator } from './ChangePlanIndicator';
import { ChangePlanSection } from './ChangePlanSection';
import { ChangePlanSummary } from './ChangePlanSummary';
import { getChangeLabel, getChangeLabelForPlanName } from './utils';

type Props = {
  /** `upgrade`, `downgrade` etc. */
  changeType: ChangeType;
  /** ISO code for selected currency */
  currency?: string;
  /** The interval selected. Should be used when controlled via `onSelectInterval` */
  interval?: Interval;
  /** Hides the header */
  isCompact?: boolean;
  /** Called when cancel button is clicked */
  onCancel: () => void;
  /** Called when change plan action is successful */
  onFinishChangePlan?: () => void;
  /** Called when interval is clicked. Passing a function here makes this a controlled component */
  onSelectInterval?: (interval: Interval) => void;
  /** Called when currency is selected. Passing a function here makes this a controlled component */
  onSelectCurrency?: (currency: string) => void;
  /** Flag to indicate whether to route to downgrade confirmation page before downgrading */
  showDowngradeConfirmation?: boolean;
  /** Flag to indicate whether to show the change plan success modal or not */
  showSuccessModal: boolean;
  /** THe name of the plan changing to */
  toPlanName: string;
};

const changePlanCss = (props: Props) =>
  css([
    css`
      background-color: ${Colors.white};
      border: ${Border.size.default} solid ${Border.color.default};
      border-radius: 5px;
      margin: 2em auto;
      max-width: 800px;
      padding: 2em;
      width: auto;
    `,
    props.isCompact &&
      css`
        border: none;
      `,
  ]);

const buttonsCss = css`
  margin-top: 2em;
`;

const errorCss = css`
  margin-top: 18px;
  text-align: center;
`;

const findPlans = (
  toPlanName: string,
  plans: PlanV2[],
  currentInterval: Interval
) => {
  const altInterval = currentInterval === 'year' ? 'month' : 'year';
  const toPlanNameLower = toPlanName.toLowerCase();

  let toPlan = plans.find(
    p =>
      toPlanNameLower === p.name.toLowerCase() && currentInterval === p.interval
  );

  toPlan =
    toPlan ??
    plans.find(
      p =>
        toPlanNameLower === p.name.toLowerCase() && altInterval === p.interval
    );

  // The alternative interval plan for the selected plan
  const toPlanAlt = plans.find(
    p =>
      toPlanNameLower === p.name.toLowerCase() &&
      p.tasks === toPlan?.tasks &&
      p.zaps === toPlan?.zaps &&
      p.interval !== toPlan.interval
  );

  return [toPlan, toPlanAlt];
};

export const ChangePlan = (props: Props) => {
  const {
    toPlanName,
    changeType,
    currency,
    interval,
    onSelectCurrency,
    onSelectInterval,
    showSuccessModal,
  } = props;
  const { routeTo } = useLink();
  const { trackEvent } = useTrackingContext();

  const { accountId } = useCurrentAccountId();
  const {
    data: { plans, currentAccount, currentPlan },
    isLoading: isLoadingPlans,
  } = usePlans(accountId, { flatPlans: true });

  const { isLoading: isLoadingPromoPlans, promoPlans } = usePromoPlans();

  // INTERVAL STATE -------------
  const [selectedInterval, setSelectedInterval] = useState<Interval>('year');
  // Current interval is props.interval if being controlled, or internal state if uncontrolled
  const currentInterval = interval ?? selectedInterval;

  // CURRENCY STATE -------------
  const {
    selectedCurrency,
    setSelectedCurrency,
    isLoading: isLoadingSelectedCurrency,
  } = useSelectedCurrency({
    currency,
    onSelectCurrency,
    switchEnabled: currentAccount?.switches?.billingMulticurrencySupport,
  });

  const { forceSourceEdit, primarySource } = usePaymentSource({
    accountId,
  });
  const { pendingInvoice } = usePendingPayment();
  const {
    currencyRuleset,
    isLoading: isLoadingCurrencyRuleset,
  } = useCurrencyRuleset();

  const isLoading =
    isLoadingPlans ||
    isLoadingPromoPlans ||
    isLoadingSelectedCurrency ||
    isLoadingCurrencyRuleset;
  const plansWithPromos = isLoading ? [] : [...plans, ...promoPlans];

  useMaybeSendFullStoryEvent(
    currentAccount?.switches?.trackUpgradesByPlan && changeType === 'upgrade',
    `Began Upgrade From Plan ${currentPlan?.id}`
  );

  const [toPlan, toPlanAlt] = findPlans(
    toPlanName,
    plansWithPromos as PlanV2[],
    currentInterval
  );

  // INVOICE PREVIEW -------------
  const {
    error: invoiceError,
    invoice,
    isLoading: isLoadingInvoice,
  } = useInvoicePreview({
    accountId: currentAccount?.id as number,
    planId: toPlan?.id,
    currency: selectedCurrency.name,
  });

  // CHANGE PLAN -------------
  const {
    changePlan,
    changePlanError,
    hasChangedPlan,
    isChangingPlan,
    pausedZaps,
  } = useChangePlan({
    accountId,
    planId: toPlan?.id,
    downgradeReason: null,
    currency: selectedCurrency.name,
    invoicedAt: invoice?.invoicedAt,
  });

  const onChangePlan = async () => {
    if (
      changeType === 'downgrade' &&
      props.showDowngradeConfirmation &&
      routeTo
    ) {
      return routeTo(
        buildDowngradeWarningUrl(
          currentPlan?.name ?? '',
          toPlanName,
          currentInterval,
          selectedCurrency.name
        )
      );
    }

    await changePlan();

    trackEvent('Billing: plan changed', {
      accountId,
      plan: toPlan?.name,
      planId: toPlan?.id,
      interval,
    });

    if (isFunction(props.onFinishChangePlan)) {
      return props.onFinishChangePlan();
    }
    return null;
  };

  const onCancelChange = () => {
    trackEvent('Billing: cancelChangePlan', {
      accountId,
      fromPlanName: currentPlan?.name,
      toPlanName: toPlan?.name,
      interval,
      changeType,
      step: 'first step',
    });

    props.onCancel();
  };

  const onSelectIntervalCallback = useCallback(
    (newInterval: Interval) => {
      return isFunction(onSelectInterval)
        ? onSelectInterval(newInterval)
        : setSelectedInterval(newInterval);
    },
    [onSelectInterval]
  );

  // Ensure selected interval and `toPlan.interval` match
  useEffect(() => {
    if (
      !isLoading &&
      plansWithPromos.length > 0 &&
      toPlan &&
      toPlan.interval !== currentInterval
    ) {
      onSelectIntervalCallback(toPlan.interval);
    }
  }, [
    currentInterval,
    isLoading,
    onSelectIntervalCallback,
    plansWithPromos,
    toPlan,
  ]);

  const changeLabel = getChangeLabel(changeType);
  const heading = getChangeLabelForPlanName(changeType, toPlan?.name ?? '');
  const subheading = `${changeLabel} account`;

  const submitText =
    changeType === 'change'
      ? `${changeLabel} Account Billing`
      : `${changeLabel} My Account`;

  const hasNoPaymentSource =
    !isFree(toPlan) && (!primarySource || forceSourceEdit);
  const hasUnsupportedPaymentSource = !currencyRuleset
    .filter(rule => rule.currency === selectedCurrency.name)
    .map(rule => rule.method)
    .includes(primarySource?.method);

  /**
   *  A user on the same plan and interval shouldn't be allowed to proceed since it's essentially a no-op
   */
  const isSamePlanAndInterval =
    currentPlan?.id === toPlan?.id &&
    currentPlan?.interval === toPlan?.interval;

  const isSubmitDisabled =
    isLoadingInvoice ||
    hasNoPaymentSource ||
    pendingInvoice ||
    hasUnsupportedPaymentSource ||
    isSamePlanAndInterval;

  const popoverText = hasNoPaymentSource
    ? 'Please add a payment method.'
    : pendingInvoice
    ? 'Cannot change plan with a pending payment.'
    : null;

  return (
    <CurrencyProvider selectedCurrency={selectedCurrency}>
      <Document title={heading}>
        {isLoading ? (
          <SkeletonBlock />
        ) : (
          <Fragment>
            {showSuccessModal && hasChangedPlan && (
              <ChangePlanCompleteModal
                changeType={changeType}
                hasPausedZaps={pausedZaps.length > 0}
                newPlan={toPlan as PlanV2}
              />
            )}
            <AsyncOverlay
              isVisible={isChangingPlan}
              renderAsyncIndicator={
                isChangingPlan ? () => <ChangePlanIndicator /> : undefined
              }
            >
              <form
                css={changePlanCss(props)}
                onSubmit={e => e.preventDefault()}
              >
                {!props.isCompact && (
                  <ChangePlanHeader
                    accountName={
                      currentAccount?.owner?.email ?? currentAccount?.name
                    }
                    companyName={currentAccount?.companyName}
                    heading={heading}
                    subheading={subheading}
                  />
                )}

                {currency &&
                  currentAccount?.switches?.billingMulticurrencySupport && (
                    <Fragment>
                      <ChangePlanCurrencyDropdown
                        onSelectCurrency={setSelectedCurrency}
                        selectedCurrency={selectedCurrency}
                      />
                      <Spacer height={20} />
                    </Fragment>
                  )}

                {pendingInvoice && (
                  <PendingPaymentWarningBar pendingInvoice={pendingInvoice} />
                )}

                <BillingOptions
                  onSelectOption={onSelectIntervalCallback}
                  planOptions={([toPlan, toPlanAlt] as PlanV2[])
                    .sort((a, b) => b.amount - a.amount)
                    .filter(p => p !== undefined)}
                  selectedOption={currentInterval}
                />

                {!isFree(toPlan) && (
                  <ChangePlanSection title="Payment Details">
                    <PaymentApp
                      editInModal={true}
                      selectedCurrency={selectedCurrency}
                    />
                  </ChangePlanSection>
                )}

                <ChangePlanSection title="Summary">
                  {toPlan && (
                    <ChangePlanSummary
                      error={invoiceError}
                      invoice={invoice}
                      isLoading={isLoadingInvoice}
                      plan={toPlan}
                    />
                  )}
                </ChangePlanSection>

                <div css={buttonsCss}>
                  <ChangePlanButtons
                    isDisabled={isSubmitDisabled}
                    onCancelChangePlan={onCancelChange}
                    onChangePlan={onChangePlan}
                    popoverText={popoverText}
                    submitText={submitText}
                  />
                </div>
                {changePlanError && (
                  <div css={errorCss}>
                    <MessageBar type="error">{changePlanError}</MessageBar>
                  </div>
                )}
              </form>
            </AsyncOverlay>
          </Fragment>
        )}
      </Document>
    </CurrencyProvider>
  );
};
